import { useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { Amplify, Auth } from 'aws-amplify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import awsExports from '../aws-exports';
import { forceRefreshSession, isTokenNearExpiry, REFRESH_CHECK_INTERVAL_MS } from '../utils/tokenRefresh';
import { DownloadProvider } from '../store/downloadManager/DownloadManagerContext';
import { SettingsContextProvider } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import Authenticated from './Authenticated';
import NotAuthenticated from './NotAuthenticated';

// import { ComponentsProviderProps } from '@aws-amplify/ui';

Amplify.configure(awsExports);

function Dashboard() {
    const { route } = useAuthenticator(context => [context.route]);
    const { user, signOut } = useAuthenticator(context => [context.user]);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [jwtToken, setJwtToken] = useState<string>('');

    const session: CognitoUserSession | null = user ? user.getSignInUserSession() : null;

    useEffect(() => {
        if (session) {
            setJwtToken(session.getAccessToken().getJwtToken());

            const groups = session.getAccessToken().payload['cognito:groups'];

            setIsAdmin(Array.isArray(groups) && groups.includes('admin'));
        }
    }, [session]);

    const refreshToken = useCallback(async (): Promise<string | null> => {
        try {
            // Auth.currentSession() silently exchanges the refresh token for a new access
            // token when the cached one is expired - no page reload needed
            const freshSession: CognitoUserSession = await Auth.currentSession();
            const accessToken = freshSession.getAccessToken();
            const freshJwtToken = accessToken.getJwtToken();

            setJwtToken(freshJwtToken);

            const groups = accessToken.payload['cognito:groups'];

            setIsAdmin(Array.isArray(groups) && groups.includes('admin'));

            return freshJwtToken;
        } catch (error) {
            // Refresh token revoked (admin global sign-out) or expired - a new sign-in is required.
            // Transient failures (e.g. network) are left alone so the next check can retry.
            if ((error as { code?: string })?.code === 'NotAuthorizedException') {
                signOut();
            } else {
                console.error('refreshToken', error);
            }

            return null;
        }
    }, [signOut]);

    // Proactively refresh shortly before expiry so long-lived pages (e.g. a form someone
    // has been filling in for an hour) never submit with an expired token. This must force
    // the exchange via refreshSession - Auth.currentSession() (refreshToken above) returns
    // the cached session untouched while it is still valid
    useEffect(() => {
        const interval = setInterval(async () => {
            if (session && isTokenNearExpiry(session.getAccessToken().getExpiration(), Date.now())) {
                try {
                    const freshSession = await forceRefreshSession(user, session);
                    const accessToken = freshSession.getAccessToken();

                    setJwtToken(accessToken.getJwtToken());

                    const groups = accessToken.payload['cognito:groups'];

                    setIsAdmin(Array.isArray(groups) && groups.includes('admin'));
                } catch (error) {
                    // Refresh token revoked (admin global sign-out) or expired - sign-in required
                    if ((error as { code?: string })?.code === 'NotAuthorizedException') {
                        signOut();
                    } else {
                        console.error('proactive token refresh', error);
                    }
                }
            }
        }, REFRESH_CHECK_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [session, user, signOut]);

    const userContextValue = useMemo(
        () => ({ route, user, jwtToken, refreshToken, signOut, isAdmin }),
        [route, user, jwtToken, refreshToken, signOut, isAdmin],
    );

    return (
        <>
            <UserContext.Provider value={userContextValue}>
                <SettingsContextProvider>
                    <DownloadProvider>{route === 'authenticated' && session ? <Authenticated /> : <NotAuthenticated />}</DownloadProvider>
                </SettingsContextProvider>
            </UserContext.Provider>
        </>
    );
}

export default Dashboard;
